<template>
  <div class="class-1">
    <!-- Header -->
    <app-header />

    <!-- Template Download/Upload Section -->
    <div class="container mt-4">
      <div class="row">
        <div class="col-12">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="card-title">Bulk Category Operations</h5>
              <div class="d-flex gap-2">
                <button 
                  type="button" 
                  class="btn btn-success" 
                  @click="downloadTemplate"
                >
                  <i class="fas fa-download"></i> Download Template
                </button>
                <div class="position-relative">
                  <button 
                    type="button" 
                    class="btn btn-primary" 
                    @click="triggerFileUpload"
                  >
                    <i class="fas fa-upload"></i> Upload & Add Categories
                  </button>
                  <input
                    type="file"
                    ref="fileInput"
                    class="d-none"
                    accept=".xlsx, .xls, .csv"
                    @change="handleTemplateUpload"
                  >
                </div>
              </div>
              <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mt-3">
                <div class="progress">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: uploadProgress + '%' }"
                    :aria-valuenow="uploadProgress" 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {{ uploadProgress }}%
                  </div>
                </div>
              </div>
              
              <!-- Upload Results Summary -->
              <div v-if="uploadResults.show" class="mt-3">
                <div class="alert" :class="uploadResults.successCount > 0 ? 'alert-success' : 'alert-warning'">
                  <h6>Upload Results:</h6>
                  <p>Successfully added: {{ uploadResults.successCount }} categories</p>
                  <p v-if="uploadResults.errorCount > 0">Failed: {{ uploadResults.errorCount }} categories</p>
                  <button 
                    v-if="uploadResults.errors.length > 0" 
                    class="btn btn-sm btn-link" 
                    @click="showErrorDetails = !showErrorDetails"
                  >
                    {{ showErrorDetails ? 'Hide' : 'Show' }} Details
                  </button>
                  <div v-if="showErrorDetails && uploadResults.errors.length > 0" class="mt-2">
                    <div v-for="(error, index) in uploadResults.errors" :key="index" class="text-danger small">
                      {{ error }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Form -->
    <div class="container mt-4">
      <div class="row">
        <div class="col-12">
          <h2>category_details</h2>
          <form @submit.prevent="submitForm">
            <!-- Name Inputs for Each Language -->
            <div class="form-row" v-for="language in languages" :key="language.id">
              <div class="form-group col-md-12">
                <label :for="`name_${language.id}`">name ({{ language.fullName }})</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="text"
                      class="form-control"
                      :id="`name_${language.id}`"
                      :placeholder="`Enter Category Name in ${language.fullName}`"
                      v-model="form.names[language.id]"
                      :ref="`name_${language.id}`"
                    >
                  </div>
                  <span class="icon1 icon2" @click="openKeypad(`name_${language.id}`)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                      <path d="M528 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h480c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48zM128 176v-32c0-8.84-7.16-16-16-16H80c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zM128 304v-32c0-8.84-7.16-16-16-16H80c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16zm96 0v-32c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16z"/>
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <!-- Printer ID Checkboxes - Dynamic -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label>Printer ID</label>
                <div>
                  <div 
                    class="form-check" 
                    v-for="printer in printers" 
                    :key="printer.id"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`printer_${printer.id}`"
                      :value="printer.id"
                      v-model="form.printerIds"
                    >
                    <label class="form-check-label" :for="`printer_${printer.id}`">
                      {{ getPrinterName(printer) }}
                    </label>
                  </div>
                  <div v-if="printers.length === 0" class="text-muted">
                    No printers available
                  </div>
                </div>
              </div>
            </div>

            <!-- ERP Warehouse Select -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="erpWarehouse">ERP Warehouse</label>
                <select
                  class="form-control"
                  id="erpWarehouse"
                  v-model="form.erpWarehouse"
                >
                  <option value="Warehouse A">Warehouse A</option>
                  <option value="Warehouse B">Warehouse B</option>
                  <option value="Warehouse C">Warehouse C</option>
                </select>
              </div>
            </div>

            <!-- Parent Category Select -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="parentCategory">Parent Category</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <select
                      class="form-control"
                      id="parentCategory"
                      v-model="form.parentId"
                      ref="parentCategory"
                    >
                      <option value="">Select a category</option>
                      <option 
                        v-for="category in groupCategories" 
                        :key="category.id" 
                        :value="category.id"
                      >
                        {{ getCategoryName(category) }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Group Checkbox -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="group"
                    v-model="form.isGroup"
                  >
                  <label class="form-check-label" for="group">Group</label>
                </div>
              </div>
            </div>

            <!-- Is Suspend Checkbox -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="isSuspend"
                    v-model="form.isSuspend"
                  >
                  <label class="form-check-label" for="isSuspend">Is Suspend</label>
                </div>
              </div>
            </div>

            <!-- Is Process Checkbox -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="is_process"
                    v-model="form.is_process"
                  >
                  <label class="form-check-label" for="is_process">Is Process</label>
                </div>
              </div>
            </div>

            <!-- Buttons -->
            <div class="form-row">
              <div class="form-group col-12 d-flex justify-content-between">
                <button type="button" class="btn btn-secondary" @click="cancelForm">Cancel</button>
                <button type="submit" class="btn addcat">Add Category</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <Keypad 
      ref="keypad"
      :target-input="currentInputRef"
      @update:value="updateInputValue"
      @cancel="closeKeypad"
    />
  </div>
</template>

<script>
import Keypad from './keypad_popup_component.vue';
import { Modal } from 'bootstrap'; 
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  components: {
    Keypad
  },
  
  data() {
    return {
      categories: [], 
      languages: [], 
      printers: [],
      form: {
        names: {}, 
        printerIds: [], 
        erpWarehouse: '',
        isGroup: false, 
        isSuspend: false,
        is_process: false, 
        parentId: '',
        categoryImage: null,
        branchId: localStorage.getItem('branchId'), 
      },
      imagePreview: null,
      currentInputRef: null,
      currentInputName: null,
      keypadModal: null,
      uploadProgress: 0,
      uploadResults: {
        show: false,
        successCount: 0,
        errorCount: 0,
        errors: []
      },
      showErrorDetails: false
    };
  },
  
  created() {
    this.fetchCategories();
    this.fetchPrinters();
    const storedLanguages = localStorage.getItem('languages');
    if (storedLanguages) {
      this.languages = JSON.parse(storedLanguages).reverse();
    }
  },
  
  mounted() {
    console.log(this.categories); 
    this.$nextTick(() => {
      const modalElement = this.$refs.keypad.$el;
      this.keypadModal = new Modal(modalElement);
    });
  },
  
  computed: {
    groupCategories() {
      return this.categories.filter(category => category.isGroup === 1);
    }
  },
  
  methods: {
    // Helper function to parse boolean values (accepts Yes/No/y/n)
    parseBooleanValue(value) {
      if (!value) return false;
      
      const strValue = value.toString().trim().toLowerCase();
      
      // Check for positive values
      if (strValue === 'yes' || strValue === 'y' || strValue === '1' || strValue === 'true') {
        return true;
      }
      
      // Check for negative values
      if (strValue === 'no' || strValue === 'n' || strValue === '0' || strValue === 'false') {
        return false;
      }
      
      // Default to false for invalid values
      return false;
    },

    // Validate boolean field (checks if it's a valid value)
    isValidBoolean(value) {
      if (!value) return true; // Empty is allowed (will default to false)
      
      const strValue = value.toString().trim().toLowerCase();
      const validValues = ['yes', 'y', 'no', 'n', '1', '0', 'true', 'false'];
      
      return validValues.includes(strValue);
    },

    async fetchPrinters() {
      const token = localStorage.getItem('authToken');
      try {
        this.$root.isLoading = true;
        const response = await axios.get(`${this.$BASE_API_URL}/api/printers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.status && response.data.data) {
          this.printers = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching printers:', error);
        this.$toast.error('Failed to load printers');
      } finally {
        this.$root.isLoading = false;
      }
    },

    getPrinterName(printer) {
      if (printer.languages && printer.languages.length > 0) {
        const englishName = printer.languages.find(lang => lang.langId === 0)?.name;
        return englishName || printer.languages[0]?.name || `Printer ${printer.id}`;
      }
      return `Printer ${printer.id}`;
    },

    getCategoryName(category) {
      if (category.languages && category.languages.length > 0) {
        return category.languages[0].name;
      }
      return `Category ${category.id}`;
    },

    async submitForm() {
      try {
        this.$root.isLoading = true; 
        const formData = new FormData();

        formData.append('isSuspend', this.form.isSuspend ? '1' : '0'); 
        formData.append('isGroup', this.form.isGroup ? '1' : '0'); 
        formData.append('ERP_warehouse', this.form.erpWarehouse);
        formData.append('branchId', this.form.branchId.toString()); 
        formData.append('parentId', this.form.parentId.toString()); 
        formData.append('is_process', this.form.is_process ? '1' : '0');
        
        this.form.printerIds.forEach((printerId) => {
          formData.append('printerIds[]', printerId.toString());
        });

        this.languages.forEach((language, index) => {
          formData.append(`names[${index}][langId]`, language.id.toString()); 
          formData.append(`names[${index}][name]`, this.form.names[language.id] || ''); 
        });

        if (this.form.categoryImage) {
          formData.append('image', this.form.categoryImage);
        }

        const token = localStorage.getItem('authToken');
        
        const isDuplicate = this.categories.some(category => {
          return this.languages.every(lang => {
            const langName = category.languages.find(l => l.langId === lang.id)?.name;
            return langName === this.form.names[lang.id];
          });
        });
        
        if (isDuplicate) {
          this.$toast.error('A category with these exact names already exists');
          return;
        }

        const response = await fetch(`${this.$BASE_API_URL}/api/categories`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json', 
          },
          body: formData, 
        });

        if (response.ok) {
          const data = await response.json();
          console.log(data);
          this.$toast.success('Category added successfully!');
          this.cancelForm(); 
        } else {
          const responseText = await response.text();
          console.log(responseText);
          this.$toast.error('Failed to add category. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        this.$toast.error('An error occurred. Please try again.');
      } finally {
        this.$root.isLoading = false;
      }
    },

    async fetchCategories() {
      const token = localStorage.getItem('authToken');
      try {
        this.$root.isLoading = true; 
        const response = await axios.get(`${this.$BASE_API_URL}/api/categories`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.status && response.data.data) {
          this.categories = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        this.$root.isLoading = false;
      }
    },

    cancelForm() {
      this.$router.go(-1);
      this.form = {
        names: {},
        printerIds: [],
        erpWarehouse: '',
        parentId: '',
        isGroup: false,
        isSuspend: false,
        is_process: false,
        categoryImage: null,
        branchId: localStorage.getItem('branchId'),
      };
    },

    openKeypad(inputRef) {
      this.currentInputRef = this.$refs[inputRef]?.[0] || this.$refs[inputRef];
      this.currentInputName = inputRef;
      this.keypadModal.show();
    },

    updateInputValue(value) {
      if (this.currentInputName) {
        if (this.currentInputName.startsWith('name_')) {
          const langId = this.currentInputName.split('_')[1];
          this.form.names[langId] = value;
        } else if (this.currentInputRef) {
          this.currentInputRef.value = value;
          if (this.currentInputName === 'parentCategory') {
            this.form.parentId = value;
          }
        }
      }
    },

    closeKeypad() {
      if (this.keypadModal) {
        this.keypadModal.hide();
      }
      this.removeBackdrop();
      this.currentInputRef = null;
      this.currentInputName = null;
    },

    removeBackdrop() {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.paddingRight = '';
    },

    handleImageUpload(event) {
      const file = event.target.files[0];
      
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        this.form.categoryImage = file;
      } else {
        this.imagePreview = null;
        this.form.categoryImage = null;
      }
    },

    // UPDATED EXCEL TEMPLATE METHODS - Single sheet only
    downloadTemplate() {
      try {
        const wb = XLSX.utils.book_new();
        
        // Create headers for all languages
        const languageHeaders = this.languages.map(lang => `Name (${lang.fullName})*`);
        
        const headers = [
          ...languageHeaders,
          'Printer IDs (comma-separated)',
          'ERP Warehouse',
          'Parent Category Name',
          'Is Group (Yes/No/y/n)',
          'Is Suspend (Yes/No/y/n)',
          'Is Process (Yes/No/y/n)'
        ];
        
        // Create worksheet with just headers
        const ws = XLSX.utils.aoa_to_sheet([headers]);
        
        // Set column widths
        ws['!cols'] = [
          ...this.languages.map(() => ({ wch: 25 })),
          { wch: 25 },
          { wch: 20 },
          { wch: 25 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 }
        ];
        
        XLSX.utils.book_append_sheet(wb, ws, 'Categories');
        
        // Generate and download file
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, 'category_bulk_upload_template.xlsx');

        this.$toast.success('Template downloaded successfully!');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$toast.error('Failed to download template');
      }
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    async handleTemplateUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Reset upload results
      this.uploadProgress = 0;
      this.uploadResults = {
        show: false,
        successCount: 0,
        errorCount: 0,
        errors: []
      };
      this.showErrorDetails = false;

      try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Get first sheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            console.log('Raw Excel data:', jsonData);
            
            // Skip header row (first row) and start from row 2
            const dataRows = jsonData.slice(1);
            
            console.log('Data rows to process:', dataRows);
            
            this.uploadProgress = 10;
            
            // Process each row
            await this.processBulkCategories(dataRows);
            
          } catch (error) {
            console.error('Error parsing Excel:', error);
            this.$toast.error('Failed to parse Excel file. Please check the format.');
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error reading file:', error);
        this.$toast.error('Failed to read file');
      } finally {
        // Clear file input
        event.target.value = '';
      }
    },

    async processBulkCategories(rows) {
      this.$root.isLoading = true;
      
      const totalRows = rows.length;
      let processed = 0;
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      console.log('Processing rows:', rows);

      for (const row of rows) {
        processed++;
        this.uploadProgress = Math.round((processed / totalRows) * 90) + 10;

        try {
          // Skip empty rows
          if (!row || row.length === 0 || row.every(cell => !cell || cell === '')) {
            console.log(`Skipping empty row ${processed}`);
            continue;
          }

          console.log(`Processing row ${processed}:`, row);

          // Parse row data
          const languageNames = row.slice(0, this.languages.length);
          
          // Check if all required language names are provided
          const missingLanguages = languageNames.filter(name => !name || name.toString().trim() === '');
          if (missingLanguages.length > 0) {
            throw new Error('All language name fields are required');
          }

          // Get other fields
          const printerIdsStr = row[this.languages.length] ? row[this.languages.length].toString() : '';
          const erpWarehouse = row[this.languages.length + 1] ? row[this.languages.length + 1].toString() : '';
          const parentName = row[this.languages.length + 2] ? row[this.languages.length + 2].toString() : '';
          const isGroup = row[this.languages.length + 3] ? row[this.languages.length + 3].toString() : 'No';
          const isSuspend = row[this.languages.length + 4] ? row[this.languages.length + 4].toString() : 'No';
          const isProcess = row[this.languages.length + 5] ? row[this.languages.length + 5].toString() : 'No';

          console.log('Parsed values:', {
            languageNames,
            printerIdsStr,
            erpWarehouse,
            parentName,
            isGroup,
            isSuspend,
            isProcess
          });

          // Validate boolean fields (now accepts various formats)
          if (!this.isValidBoolean(isGroup)) {
            throw new Error(`Is Group value "${isGroup}" is not valid. Use Yes/No/y/n`);
          }
          if (!this.isValidBoolean(isSuspend)) {
            throw new Error(`Is Suspend value "${isSuspend}" is not valid. Use Yes/No/y/n`);
          }
          if (!this.isValidBoolean(isProcess)) {
            throw new Error(`Is Process value "${isProcess}" is not valid. Use Yes/No/y/n`);
          }

          // Prepare form data
          const formData = new FormData();
          
          // Add language names
          this.languages.forEach((lang, index) => {
            if (languageNames[index] && languageNames[index].toString().trim()) {
              formData.append(`names[${index}][langId]`, lang.id.toString());
              formData.append(`names[${index}][name]`, languageNames[index].toString().trim());
            }
          });

          // Parse and add printer IDs
          if (printerIdsStr) {
            const printerIds = printerIdsStr.split(',')
              .map(id => id.trim())
              .filter(id => id && !isNaN(id));
            
            printerIds.forEach(printerId => {
              formData.append('printerIds[]', printerId.toString());
            });
          }

          // Add other fields using the parseBooleanValue helper
          formData.append('ERP_warehouse', erpWarehouse.trim());
          formData.append('branchId', this.form.branchId.toString());
          formData.append('isGroup', this.parseBooleanValue(isGroup) ? '1' : '0');
          formData.append('isSuspend', this.parseBooleanValue(isSuspend) ? '1' : '0');
          formData.append('is_process', this.parseBooleanValue(isProcess) ? '1' : '0');

          // Find parent ID if parent name provided
          if (parentName && parentName.trim()) {
            const parentCategory = this.categories.find(cat => 
              cat.languages.some(lang => 
                lang.name.toLowerCase() === parentName.trim().toLowerCase()
              )
            );
            
            if (parentCategory) {
              formData.append('parentId', parentCategory.id.toString());
            } else {
              console.warn(`Parent category "${parentName}" not found, leaving empty`);
            }
          }

          // Check for duplicate
          const isDuplicate = this.categories.some(category => {
            return this.languages.every(lang => {
              const langName = category.languages.find(l => l.langId === lang.id)?.name;
              const langIndex = this.languages.findIndex(l => l.id === lang.id);
              const currentLangName = languageNames[langIndex]?.toString().trim();
              return langName === currentLangName;
            });
          });

          if (isDuplicate) {
            throw new Error('Category with these exact names already exists');
          }

          // Submit category
          const token = localStorage.getItem('authToken');
          console.log('Sending request to API...');
          
          const response = await fetch(`${this.$BASE_API_URL}/api/categories`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
            body: formData,
          });

          console.log('Response status:', response.status);

          if (response.ok) {
            successCount++;
            console.log(`Category ${processed} added successfully`);
          } else {
            const errorText = await response.text();
            console.error('API Error response:', errorText);
            throw new Error(`API Error: ${errorText}`);
          }

        } catch (error) {
          errorCount++;
          errors.push(`Row ${processed + 1}: ${error.message}`);
          console.error(`Error processing row ${processed + 1}:`, error);
        }
      }

      // Update results
      this.uploadResults = {
        show: true,
        successCount,
        errorCount,
        errors
      };

      this.$root.isLoading = false;
      this.uploadProgress = 100;

      // Show summary toast
      if (errorCount === 0 && successCount > 0) {
        this.$toast.success(`Successfully added ${successCount} categories!`);
      } else if (successCount > 0 && errorCount > 0) {
        this.$toast.warning(`Added ${successCount} categories, ${errorCount} failed. Check details for more information.`);
      } else if (successCount === 0 && errorCount > 0) {
        this.$toast.error(`Failed to add any categories. ${errorCount} errors occurred.`);
      } else if (successCount === 0 && errorCount === 0) {
        this.$toast.info('No valid data rows found in the uploaded file.');
      }

      // Refresh categories list
      await this.fetchCategories();
    }
  }
};
</script>

<style scoped>
.searchbyphone {
  position: relative;
}

.infobox.fieldOuter {
  position: relative;
  width: 100%;
}

.icon1.icon2 {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 38px;
  background: #f8f9fa;
  border: 1px solid #ced4da;
  border-left: none;
  cursor: pointer;
}

.icon1.icon2 svg {
  width: 20px;
  height: 20px;
  fill: #495057;
}

.form-control {
  border-radius: 0.25rem 0 0 0.25rem !important;
}

.class-1 {
  font-family: 'Poppins', sans-serif;
}

.logo_icon {
  max-width: 100px;
}

.addcat {
  background-color: #4caf50;
  color: #fff;
}

.gap-2 {
  gap: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.progress {
  height: 20px;
  border-radius: 4px;
}

.progress-bar {
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.card {
  background-color: transparent;
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-title {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 500;
}

.position-relative {
  position: relative;
}

.d-none {
  display: none;
}

.alert {
  padding: 1rem;
  border-radius: 0.25rem;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeeba;
  color: #856404;
}

.btn-link {
  color: #007bff;
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

.text-danger {
  color: #dc3545;
}

.small {
  font-size: 0.875rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}
</style>